<template>
  <div class="t-sidebar" :class="{ collapse: showMenuCollapse }">
    <div class="t-sidebar-content">
      <div class="t-sidebar-logo">
        <slot v-if="!showMenuCollapse" name="t-sidebar-logo" />
        <slot v-else name="t-sidebar-logo-collapse" />
      </div>

      <div class="t-sidebar-menu">
        <slot name="t-sidebar-menu--primary">
          <SidebarMenuItem
            v-for="(menu, index) in primaryMenu"
            :key="index"
            :is-collapse="showMenuCollapse"
            :menu-item="menu"
            :current-route-name="currentRouteName"
            :is-active="menu.pathName === currentRouteName"
            @onClickMenuItem="
              (menuItem) =>
                onClickMenuItem({
                  type: 'primary',
                  menuItem,
                  index
                })
            "
            @onClickChildNavItem="
              (childItemPayload) =>
                onClickChildNavItem({
                  type: 'primary',
                  ...childItemPayload
                })
            "
          >
            <template #t-sidebar-menu-icon>
              <span v-if="menu.iconClass.includes('scale-icon')" v-html="menu.iconClass" />
              <scale-icon
                v-else
                :size="menu.iconSize || 20"
                :class="menu.iconClass"
                :name="menu.iconClass"
              />
            </template>
          </SidebarMenuItem>
        </slot>
      </div>

      <div class="t-sidebar-menu bottom-menu">
        <slot name="t-sidebar-menu-secondary">
          <template v-if="secondaryMenu?.length">
            <SidebarMenuItem
              v-for="(menu, index) in secondaryMenu"
              :key="index"
              :is-collapse="showMenuCollapse"
              :menu-item="menu"
              :current-route-name="currentRouteName"
              :is-active="menu.pathName === currentRouteName"
              @onClickMenuItem="
                (menuItem) =>
                  onClickMenuItem({
                    type: 'secondary',
                    menuItem,
                    index
                  })
              "
              @onClickChildNavItem="
                (childItemPayload) =>
                  onClickChildNavItem({
                    type: 'secondary',
                    ...childItemPayload
                  })
              "
            >
              <template #t-sidebar-menu-icon>
                <scale-icon size="20" :class="menu.iconClass" :name="menu.iconClass" />
              </template>
            </SidebarMenuItem>
          </template>
        </slot>

        <SidebarMenuItem
          v-if="showCollapse"
          :isCollapse="showMenuCollapse"
          :menu-item="collapseMenuItem"
          @onClickMenuItem="onClickCollapse"
        >
          <template #t-sidebar-menu-icon>
            <div :class="{ 'is-icon-rotate': showMenuCollapse }">
              <scale-icon size="20" :name="collapseMenuItem.iconClass" />
            </div>
          </template>
        </SidebarMenuItem>

        <SidebarFooter :is-sidebar-open="!showMenuCollapse" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from "vue"

import SidebarMenuItem from "./SidebarMenuItem.vue"
import SidebarFooter from "./SidebarFooter.vue"
import {
  type IntMenuItem,
  type IntMenuItemEmitPayload,
  type IntChildEmitPayload,
  defaultCollapseMenuItem
} from "./IntSidebarNav"

const emit = defineEmits<{
  (e: "onClickCollapse"): void
  (e: "onClickMenuItem", payload: IntMenuItemEmitPayload): void
  (e: "onClickChildMenuItem", payload: IntChildEmitPayload): void
}>()

defineProps({
  primaryMenu: {
    type: Array as PropType<IntMenuItem[]>,
    default: () => []
  },
  secondaryMenu: {
    type: Array as PropType<IntMenuItem[]>,
    default: () => []
  },
  showMenuCollapse: {
    type: Boolean,
    default: false
  },
  showCollapse: {
    type: Boolean,
    default: true
  },
  currentRouteName: {
    type: String,
    default: null
  },
  collapseMenuItem: {
    type: Object as PropType<IntMenuItem>,
    default: defaultCollapseMenuItem
  }
})

function onClickCollapse(): void {
  emit("onClickCollapse")
}

function onClickMenuItem(payload: IntMenuItemEmitPayload): void {
  emit("onClickMenuItem", payload)
}

function onClickChildNavItem(payload: IntChildEmitPayload) {
  emit("onClickChildMenuItem", payload)
}
</script>

<style lang="scss" scoped>
@import "@/styles/utilities/variables";
@import "@/styles/utilities/mixins";

$tsidebar-menu-width: $t-sidebar-width !default;
$tsidebar-menu-collapse-width: $t-sidebar-width-collapse !default;
$menu-item-padding-left: calc(var(--scl-spacing-24) * 3);
$menu-item-content-min-height: calc(var(--scl-spacing-24) * 3);
.t-sidebar {
  position: fixed;
  width: $tsidebar-menu-width;
  top: $nav-bar-height;
  left: 0;
  top: 0;
  z-index: 50;
  bottom: 0;
  background-color: var(--telekom-color-background-canvas-subtle);
  overflow-y: auto;
  transition: width 0.2s ease-in-out;
  border-right: 1px solid var(--telekom-color-ui-faint);

  @include custom-scroll-thin;

  &-content {
    display: flex;
    height: 100%;
    flex-direction: column;
  }

  .t-sidebar-logo {
    padding: 0 var(--scl-spacing-16);
    white-space: nowrap;
    height: $nav-bar-height;
    min-height: $nav-bar-height;
    display: flex;
    background-color: var(--dih-telekom-background-surface-subtle);

    .nav-bar-logo {
      max-width: 100%;
    }
  }

  .t-sidebar-menu {
    margin-top: var(--scl-spacing-16);
    &.bottom-menu {
      margin-top: auto;
    }
  }

  &.collapse {
    width: $tsidebar-menu-collapse-width;
    overflow-x: hidden;

    .t-sidebar-logo {
      padding-left: var(--scl-spacing-16);
    }
  }

  .is-icon-rotate {
    transform: rotate(180deg);
  }
}
</style>
